/**
 * NeoCoreInscribirAsociadoPage
 * ─────────────────────────────────────────────────────────────────────
 * Wizard web del operador para inscribir un asociado nuevo. Reemplaza
 * al modal `CrearAsociadoModal` (que solo creaba el row de asociado sin
 * auth user) y usa la misma edge function `inscripcion-digital` del
 * app móvil — un solo backend, dos UIs.
 *
 * Flujo (3 pasos visibles · 2 transiciones de espera):
 *
 *   ①  Capturar 3 documentos
 *      → drag&drop, paste desde clipboard, o input file. El operador no
 *        tipea DPI — sale del OCR.
 *
 *   ⏳ Procesando OCR
 *      → llama edge function `kyc-ocr-verify` con las 3 imágenes.
 *        Recupera 22 campos (15 FEIS + 7 domicilio) + DPI + MRZ.
 *        Después hace lookup automático contra `asociado` para duplicados.
 *
 *   ②  Revisar y completar
 *      → split: imágenes a la izq, formulario a la derecha. Datos del
 *        OCR pre-llenos. Operador SOLO llena los 5 campos operativos
 *        (profesión, actividad, origen fondos, ingresos, PEP).
 *
 *   ⏳ Creando asociado
 *      → llama edge function `inscripcion-digital`. Genera PIN aleatorio
 *        con must_change_password=true para que el asociado lo cambie
 *        al primer login del móvil.
 *
 *   ③  Credenciales
 *      → card imprimible con DPI + PIN provisional + link a la ficha.
 *
 * Reemplazos respecto al modal viejo:
 *   - Auth user real (vía edge function) en lugar de solo `asociado` row
 *   - PIN provisional + must_change_password (asociado lo cambia)
 *   - UX wizard página completa en lugar de modal saturado
 *   - Captura paste/drag/click (no solo input file)
 *
 * El OCR usa el motor de visión propio (edge function kyc-ocr-verify).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft, ArrowRight, CheckCircle,
  Sparkle, Copy, Printer, Warning, X, ShieldCheck, ArrowsClockwise,
  IdentificationCard, Receipt, User, FilePdf, DeviceMobile,
  Briefcase, Storefront, Airplane, Bank, Armchair, DotsThree,
  Star, Check, CaretDown,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { parseEdgeError, formatEdgeError } from '@/lib/parseEdgeError'
import type { EstadoCivil, Genero, OrigenFondos } from '@/lib/typesNeoCore'
import { ProfesionSelect, ActividadEconomicaSelect } from '@/components/ProfesionSelect'
import { RegistrarInscripcionModal } from '@/components/RegistrarInscripcionModal'
import { PAISES, PAIS_DEFAULT, armarE164, detectarPais, digitosNacionales, type Pais } from '@/lib/paises'

/**
 * Rangos de ingreso mensual — mismos que la app móvil (confirmar-contacto.tsx).
 * El IVE pide franja, no monto exacto. Capturamos código (`<2500`, `2500_5000`, ...)
 * en `ingresos_mensuales_aprox` y lo mostramos como label legible al operador.
 */
const RANGOS_INGRESO: Array<{ value: string; label: string }> = [
  { value: '<2500',        label: 'Menos de Q 2,500' },
  { value: '2500_5000',    label: 'Q 2,500 — Q 5,000' },
  { value: '5000_10000',   label: 'Q 5,000 — Q 10,000' },
  { value: '10000_25000',  label: 'Q 10,000 — Q 25,000' },
  { value: '25000_50000',  label: 'Q 25,000 — Q 50,000' },
  { value: '>50000',       label: 'Más de Q 50,000' },
]

// ════════════════════════════════════════════════════════════════════
// Tipos
// ════════════════════════════════════════════════════════════════════

type DocSlot = 'dpi_frente' | 'dpi_dorso' | 'recibo'
type Step = 'captura' | 'procesando' | 'duplicado' | 'revisar' | 'creando' | 'listo' | 'activo'

type FieldSource = 'mrz' | 'frente' | 'dorso' | 'recibo' | 'manual'

type OcrExtracted = {
  primer_nombre: string
  segundo_nombre: string
  otros_nombres: string
  primer_apellido: string
  segundo_apellido: string
  apellido_casada: string
  numero_documento: string
  fecha_nacimiento: string
  nacionalidad: string
  sexo: Genero | ''
  pais_nacimiento: string
  departamento_nacimiento: string
  municipio_nacimiento: string
  estado_civil: EstadoCivil | ''
  vigencia_documento: string
  profesion_oficio: string
  recibo_titular: string
  direccion_completa: string
  zona: string
  colonia: string
  municipio: string
  departamento: string
  recibo_fecha_emision: string
  recibo_tipo: string
  _meta: {
    sources: Record<string, FieldSource>
    mrz: {
      valido: boolean
      errores: string[]
      raw_lines: [string, string, string] | null
      document_check_valid: boolean
      birth_check_valid: boolean
      expiry_check_valid: boolean
      composite_check_valid: boolean
    }
    discrepancias: Array<{ campo: string; mrz: string; ocr: string; mensaje: string }>
  }
  _storage?: {
    kyc_session_id: string
    dpi_frente_url: string
    dpi_dorso_url: string
    recibo_servicios_url: string
  }
}

type FormOperativo = {
  // FEIS editables (precargados del OCR)
  primer_nombre: string
  segundo_nombre: string
  otros_nombres: string
  primer_apellido: string
  segundo_apellido: string
  apellido_casada: string
  fecha_nacimiento: string
  nacionalidad: string
  sexo: Genero | ''
  pais_nacimiento: string
  departamento_nacimiento: string
  municipio_nacimiento: string
  estado_civil: EstadoCivil | ''
  vigencia_documento: string
  direccion_completa: string
  zona: string
  municipio: string
  departamento: string
  // Operativos (solo el operador los llena)
  profesion_oficio: string
  actividad_economica: string
  origen_fondos: OrigenFondos
  ingresos_mensuales_aprox: string
  es_pep: boolean
  es_cpe: boolean
  es_familiar_pep: boolean
  email: string
  telefono_movil: string
}

const FORM_INITIAL: FormOperativo = {
  primer_nombre: '', segundo_nombre: '', otros_nombres: '',
  primer_apellido: '', segundo_apellido: '', apellido_casada: '',
  fecha_nacimiento: '', nacionalidad: 'GUATEMALTECA', sexo: '',
  pais_nacimiento: 'GUATEMALA',
  departamento_nacimiento: '', municipio_nacimiento: '',
  estado_civil: '', vigencia_documento: '',
  direccion_completa: '', zona: '',
  municipio: 'GUATEMALA', departamento: 'GUATEMALA',
  profesion_oficio: '',
  actividad_economica: '',
  origen_fondos: 'SALARIO',
  ingresos_mensuales_aprox: '',
  es_pep: false, es_cpe: false, es_familiar_pep: false,
  email: '', telefono_movil: '',
}

// ════════════════════════════════════════════════════════════════════
// Utilidades
// ════════════════════════════════════════════════════════════════════

/** Formatea DPI como "2081 58898 1401". */
function formatCui(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 13)
  return [d.slice(0, 4), d.slice(4, 9), d.slice(9, 13)].filter(Boolean).join(' ')
}

/** PIN provisional aleatorio de 6 dígitos. Asociado lo cambia al primer login. */
function generarPin(): string {
  return String(Math.floor(100000 + Math.random() * 900000))
}

/** Detecta si el archivo es PDF. */
function isPdf(file: File | null): boolean {
  return !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name))
}

const DOC_LABELS: Record<DocSlot, { titulo: string; sub: string; Icon: typeof IdentificationCard }> = {
  dpi_frente: { titulo: 'DPI · Frente', sub: 'Lado de la foto · imagen o PDF',  Icon: IdentificationCard },
  dpi_dorso:  { titulo: 'DPI · Dorso',  sub: 'Lado del MRZ · imagen o PDF',     Icon: IdentificationCard },
  recibo:     { titulo: 'Recibo de servicios', sub: 'Agua, luz, internet · imagen o PDF', Icon: Receipt },
}

const ACCEPT_TYPES = 'image/png,image/jpeg,image/webp,application/pdf'

// ════════════════════════════════════════════════════════════════════
// Componente principal
// ════════════════════════════════════════════════════════════════════

export function NeoCoreInscribirAsociadoPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('captura')
  const [images, setImages] = useState<Partial<Record<DocSlot, File>>>({})
  const [previewUrls, setPreviewUrls] = useState<Partial<Record<DocSlot, string>>>({})
  const [extracted, setExtracted] = useState<OcrExtracted | null>(null)
  const [form, setForm] = useState<FormOperativo>(FORM_INITIAL)
  const [pin, setPin] = useState<string>(() => generarPin())
  const [error, setError] = useState<string | null>(null)
  const [duplicado, setDuplicado] = useState<{ id: string; nombre: string; no: string } | null>(null)
  // El neoId (= no_asociado) lo asigna el backend al crear, es un random de
  // 6 dígitos generado por generar_neo_id(). Acá guardamos lo que devuelve.
  const [resultado, setResultado] = useState<{ asociadoId: string; dpi: string; pin: string; email: string; neoId: string } | null>(null)
  // Modal de cobro de inscripción (Q50 cuota + Q50 aportación). Se abre desde
  // el paso "listo" cuando el operador hace click en "Cobrar inscripción".
  const [mostrarCobro, setMostrarCobro] = useState(false)

  // Cleanup de previews al desmontar
  useEffect(() => () => Object.values(previewUrls).forEach(u => u && URL.revokeObjectURL(u)), [])

  const setImage = useCallback((slot: DocSlot, file: File) => {
    setImages(prev => {
      const old = previewUrls[slot]
      if (old) URL.revokeObjectURL(old)
      return { ...prev, [slot]: file }
    })
    setPreviewUrls(prev => ({ ...prev, [slot]: URL.createObjectURL(file) }))
    setError(null)
  }, [previewUrls])

  const removeImage = useCallback((slot: DocSlot) => {
    setImages(prev => { const { [slot]: _, ...rest } = prev; return rest })
    setPreviewUrls(prev => {
      const u = prev[slot]; if (u) URL.revokeObjectURL(u)
      const { [slot]: _, ...rest } = prev
      return rest
    })
  }, [])

  const allDocsReady = !!images.dpi_frente && !!images.dpi_dorso && !!images.recibo

  // ──────────────────────────────────────────────────────────────────
  // Paso 1 → OCR
  // ──────────────────────────────────────────────────────────────────

  const procesarOcr = useCallback(async () => {
    setStep('procesando'); setError(null)
    try {
      const fd = new FormData()
      if (images.dpi_frente) fd.append('dpi_frente', images.dpi_frente)
      if (images.dpi_dorso)  fd.append('dpi_dorso', images.dpi_dorso)
      if (images.recibo)     fd.append('recibo', images.recibo)
      // dpi_tipeado vacío: el OCR lo extrae solo (el operador NO tipea)
      fd.append('dpi_tipeado', '')
      const { data, error: ocrErr } = await supabase.functions.invoke('kyc-ocr-verify', { body: fd })
      if (ocrErr) {
        const parsed = await parseEdgeError(ocrErr)
        console.log('[NeoCoreInscribir] OCR error parsed:', parsed)
        setError(formatEdgeError(parsed))
        setStep('captura')
        return
      }
      const result = data as OcrExtracted

      // Validar que extrajo DPI
      const dpiExtraido = (result.numero_documento || '').replace(/\D/g, '')
      if (dpiExtraido.length !== 13) {
        throw new Error('El OCR no pudo leer un DPI válido. Tomá fotos más nítidas.')
      }

      // Lookup duplicado por DPI
      const { data: dup } = await supabase
        .from('asociado')
        .select('id, nombre_completo, no_asociado')
        .eq('dpi', dpiExtraido)
        .maybeSingle()

      if (dup) {
        setDuplicado({ id: dup.id, nombre: dup.nombre_completo, no: dup.no_asociado })
        setStep('duplicado')
        return
      }

      // Sin duplicado: pasamos a revisar con datos pre-llenos
      setExtracted(result)
      setForm(f => ({
        ...f,
        primer_nombre: result.primer_nombre,
        segundo_nombre: result.segundo_nombre,
        otros_nombres: result.otros_nombres,
        primer_apellido: result.primer_apellido,
        segundo_apellido: result.segundo_apellido,
        apellido_casada: result.apellido_casada,
        fecha_nacimiento: result.fecha_nacimiento,
        nacionalidad: result.nacionalidad || 'GUATEMALTECA',
        sexo: result.sexo,
        pais_nacimiento: result.pais_nacimiento || 'GUATEMALA',
        departamento_nacimiento: result.departamento_nacimiento,
        municipio_nacimiento: result.municipio_nacimiento,
        estado_civil: result.estado_civil,
        vigencia_documento: result.vigencia_documento,
        direccion_completa: result.direccion_completa,
        zona: result.zona,
        municipio: result.municipio || 'GUATEMALA',
        departamento: result.departamento || 'GUATEMALA',
        profesion_oficio: result.profesion_oficio,
      }))
      setStep('revisar')
    } catch (e: any) {
      // Excepción no-network (ej: validación local, verificarDpi fail).
      console.warn('[NeoCoreInscribir] procesarOcr excepción:', e)
      setError(e?.message ?? 'No pudimos procesar los documentos. Reintentá.')
      setStep('captura')
    }
  }, [images])

  // ──────────────────────────────────────────────────────────────────
  // Paso 2 → Submit (inscripción digital + auth user)
  // ──────────────────────────────────────────────────────────────────

  const validarForm = (): string | null => {
    if (!extracted) return 'Falta el OCR. Volvé a empezar.'
    if (!form.primer_nombre.trim()) return 'Falta el primer nombre.'
    if (!form.primer_apellido.trim()) return 'Falta el primer apellido.'
    if (!form.fecha_nacimiento) return 'Falta la fecha de nacimiento.'
    if (!form.sexo) return 'Falta el sexo.'
    if (!form.departamento_nacimiento.trim()) return 'Falta el departamento de nacimiento.'
    if (!form.municipio_nacimiento.trim()) return 'Falta el municipio de nacimiento.'
    if (!form.estado_civil) return 'Falta el estado civil.'
    if (!form.vigencia_documento) return 'Falta la vigencia del DPI.'
    if (!form.direccion_completa.trim()) return 'Falta la dirección.'
    if (!form.profesion_oficio.trim()) return 'Falta profesión u oficio.'
    if (!form.actividad_economica.trim()) return 'Falta actividad económica.'
    if (!pin || pin.length !== 6) return 'PIN debe ser 6 dígitos.'
    return null
  }

  const crearAsociado = useCallback(async () => {
    setError(null)
    const err = validarForm()
    if (err) { setError(err); return }
    if (!extracted) return

    setStep('creando')

    const body = {
      dpi: extracted.numero_documento,
      pin,
      email: form.email || undefined,
      // No mandamos no_asociado override. El backend deja no_asociado = neo_id
      // (6 dígitos random de generar_neo_id). Es la convención del proyecto:
      // No. asociado === Neo ID === identificador único de 6 dígitos.
      onboarding_origen: 'admin',
      must_change_password: true,
      feis: {
        primer_nombre: form.primer_nombre.toUpperCase(),
        segundo_nombre: form.segundo_nombre.toUpperCase() || null,
        otros_nombres: form.otros_nombres.toUpperCase() || null,
        primer_apellido: form.primer_apellido.toUpperCase(),
        segundo_apellido: form.segundo_apellido.toUpperCase() || null,
        apellido_casada: form.apellido_casada.toUpperCase() || null,
        fecha_nacimiento: form.fecha_nacimiento,
        nacionalidad: form.nacionalidad.toUpperCase(),
        genero: form.sexo,
        pais_nacimiento: form.pais_nacimiento.toUpperCase() || null,
        departamento_nacimiento: form.departamento_nacimiento.toUpperCase() || null,
        municipio_nacimiento: form.municipio_nacimiento.toUpperCase() || null,
        estado_civil: form.estado_civil || null,
        vigencia_documento: form.vigencia_documento || null,
        direccion_completa: form.direccion_completa,
        zona: form.zona || null,
        municipio: form.municipio,
        departamento: form.departamento,
        profesion_oficio: form.profesion_oficio.toUpperCase(),
        actividad_economica: form.actividad_economica.toUpperCase(),
        origen_fondos: form.origen_fondos,
        ingresos_mensuales_aprox: form.ingresos_mensuales_aprox || null,
        proposito_relacion: 'AHORRO',
        es_pep: form.es_pep,
        es_cpe: form.es_cpe,
        es_familiar_pep: form.es_familiar_pep,
        telefono: form.telefono_movil || undefined,
      },
      contactabilidad: {
        email_principal: form.email || undefined,
        telefono_movil_e164: form.telefono_movil || undefined,
      },
      ...(extracted._storage ? {
        kyc: {
          kyc_session_id: extracted._storage.kyc_session_id,
          dpi_frente_url: extracted._storage.dpi_frente_url,
          dpi_dorso_url:  extracted._storage.dpi_dorso_url,
          recibo_servicios_url: extracted._storage.recibo_servicios_url,
          recibo_servicios_tipo: extracted.recibo_tipo || null,
          recibo_servicios_fecha: extracted.recibo_fecha_emision || null,
          datos_extraidos: {
            sources: extracted._meta.sources,
            mrz_valido: extracted._meta.mrz.valido,
            mrz_document_check: extracted._meta.mrz.document_check_valid,
            mrz_birth_check: extracted._meta.mrz.birth_check_valid,
            mrz_expiry_check: extracted._meta.mrz.expiry_check_valid,
            mrz_composite_check: extracted._meta.mrz.composite_check_valid,
            mrz_raw_lines: extracted._meta.mrz.raw_lines,
            discrepancias: extracted._meta.discrepancias,
          },
        },
      } : {}),
    }

    const { data, error: invErr } = await supabase.functions.invoke('inscripcion-digital', { body })
    if (invErr) {
      let msg = invErr.message ?? 'No se pudo crear el asociado'
      const ctx: any = invErr
      if (ctx?.context?.json) {
        try { const j = await ctx.context.json(); msg = j.detail || j.error || msg } catch {}
      }
      setError(msg)
      setStep('revisar')
      return
    }
    const result = data as { asociado_id: string; email: string; neo_id: string }
    setResultado({
      asociadoId: result.asociado_id,
      dpi: extracted.numero_documento,
      pin,
      email: result.email,
      neoId: result.neo_id,
    })
    setStep('listo')
  }, [extracted, form, pin])

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────

  return (
    <div className="inscribir-page">
      {/* Header */}
      <header className="inscribir-header">
        <button className="back-btn" onClick={() => navigate('/neocore/asociados')}>
          <ArrowLeft size={18} /> Volver a asociados
        </button>
        <div className="inscribir-title">
          <h1>Inscribir asociado nuevo</h1>
          <p>Capturá los 3 documentos. El sistema extrae los datos y crea el acceso al app.</p>
        </div>
      </header>

      {/* Stepper */}
      <Stepper step={step} />

      {/* Cuerpo */}
      <main className="inscribir-body">
        {step === 'captura' && (
          <PasoCaptura
            images={images}
            previewUrls={previewUrls}
            onPick={setImage}
            onRemove={removeImage}
            onContinuar={procesarOcr}
            allReady={allDocsReady}
            error={error}
          />
        )}
        {step === 'procesando' && <PasoProcesando />}
        {step === 'duplicado' && duplicado && (
          <PasoDuplicado
            dup={duplicado}
            onVerFicha={() => navigate(`/neocore/asociados/${duplicado.id}`)}
            onReintentar={() => { setDuplicado(null); setStep('captura') }}
          />
        )}
        {step === 'revisar' && extracted && (
          <PasoRevisar
            extracted={extracted}
            images={images}
            previewUrls={previewUrls}
            form={form}
            setForm={setForm}
            pin={pin}
            onRegenerarPin={() => setPin(generarPin())}
            onVolver={() => setStep('captura')}
            onCrear={crearAsociado}
            error={error}
          />
        )}
        {step === 'creando' && <PasoCreando />}
        {step === 'listo' && resultado && (
          <PasoCredenciales
            resultado={resultado}
            nombreCompleto={`${form.primer_nombre} ${form.primer_apellido}`.trim()}
            onCobrar={() => setMostrarCobro(true)}
            onSaltarCobro={() => navigate(`/neocore/asociados/${resultado.asociadoId}`)}
          />
        )}
        {step === 'activo' && resultado && (
          <PasoActivo
            resultado={resultado}
            onVerFicha={() => navigate(`/neocore/asociados/${resultado.asociadoId}`)}
            onNuevoAsociado={() => window.location.reload()}
          />
        )}
      </main>

      {/* Modal de cobro de inscripción (Q50 cuota + Q50 aportación) */}
      {resultado && (
        <RegistrarInscripcionModal
          open={mostrarCobro}
          onClose={() => setMostrarCobro(false)}
          asociadoId={resultado.asociadoId}
          nombreCompleto={`${form.primer_nombre} ${form.primer_apellido}`.trim() || 'Asociado'}
          noAsociado={resultado.neoId}
          neoId={resultado.neoId}
          dpi={resultado.dpi}
          onSuccess={() => {
            // El modal queda abierto en vista de éxito; cuando lo cierre,
            // pasamos a la pantalla "activo" del wizard.
            setStep('activo')
          }}
        />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Stepper
// ════════════════════════════════════════════════════════════════════

function Stepper({ step }: { step: Step }) {
  const items: Array<{ key: Step | 'group'; label: string; group?: Step[] }> = [
    { key: 'captura', label: '1 · Capturar documentos' },
    { key: 'group', label: '2 · Revisar y completar', group: ['procesando', 'duplicado', 'revisar'] },
    { key: 'listo', label: '3 · Credenciales' },
    { key: 'activo', label: '4 · Cobrar inscripción' },
  ]
  const isActive = (it: typeof items[number]) => {
    if (it.group) return it.group.includes(step)
    if (it.key === 'listo') return step === 'listo' || step === 'creando'
    return step === it.key
  }
  const isDone = (it: typeof items[number]) => {
    if (it.key === 'captura') return ['procesando', 'duplicado', 'revisar', 'creando', 'listo', 'activo'].includes(step)
    if (it.group) return ['creando', 'listo', 'activo'].includes(step)
    if (it.key === 'listo') return step === 'activo'
    return false
  }
  return (
    <nav className="inscribir-stepper">
      {items.map((it, i) => (
        <div key={i} className={`step ${isActive(it) ? 'active' : ''} ${isDone(it) ? 'done' : ''}`}>
          <span className="step-num">{isDone(it) ? <CheckCircle size={16} weight="fill" /> : i + 1}</span>
          <span className="step-label">{it.label}</span>
        </div>
      ))}
    </nav>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso 1 · Captura de documentos
// ════════════════════════════════════════════════════════════════════

type PasoCapturaProps = {
  images: Partial<Record<DocSlot, File>>
  previewUrls: Partial<Record<DocSlot, string>>
  onPick: (slot: DocSlot, file: File) => void
  onRemove: (slot: DocSlot) => void
  onContinuar: () => void
  allReady: boolean
  error: string | null
}

function PasoCaptura({ images, previewUrls, onPick, onRemove, onContinuar, allReady, error }: PasoCapturaProps) {
  return (
    <div className="paso paso-captura">
      <header className="paso-header">
        <h2>Subí los 3 documentos del asociado</h2>
        <p>Aceptamos arrastrar, pegar (Cmd+V) o seleccionar archivo. PNG o JPG, máximo 10 MB cada uno.</p>
      </header>

      <div className="docs-grid">
        {(['dpi_frente', 'dpi_dorso', 'recibo'] as DocSlot[]).map(slot => (
          <DocDropZone
            key={slot}
            slot={slot}
            file={images[slot] || null}
            previewUrl={previewUrls[slot] || null}
            onPick={(f) => onPick(slot, f)}
            onRemove={() => onRemove(slot)}
          />
        ))}
      </div>

      {error && <div className="alert alert-error"><Warning size={18} /> {error}</div>}

      <footer className="paso-footer">
        <div className="hint">
          {allReady ? (
            <><CheckCircle size={16} weight="fill" /> Los 3 documentos están listos. Click para procesar con OCR.</>
          ) : (
            <>Te faltan {3 - Object.keys(images).length} documento(s)</>
          )}
        </div>
        <button className="btn-primary" disabled={!allReady} onClick={onContinuar}>
          <Sparkle size={18} weight="fill" /> Procesar con OCR <ArrowRight size={18} />
        </button>
      </footer>
    </div>
  )
}

function DocDropZone({
  slot, file, previewUrl, onPick, onRemove,
}: {
  slot: DocSlot
  file: File | null
  previewUrl: string | null
  onPick: (file: File) => void
  onRemove: () => void
}) {
  const { titulo, sub, Icon } = DOC_LABELS[slot]
  const [drag, setDrag] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const zoneRef = useRef<HTMLDivElement>(null)

  // Paste handler (cuando el zone tiene foco)
  useEffect(() => {
    const zone = zoneRef.current
    if (!zone) return
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile()
          if (f) { onPick(f); e.preventDefault(); return }
        }
      }
    }
    zone.addEventListener('paste', onPaste)
    return () => zone.removeEventListener('paste', onPaste)
  }, [onPick])

  const acceptFile = (f: File) => {
    const ok =
      f.type === 'application/pdf' ||
      /\.pdf$/i.test(f.name) ||
      f.type.startsWith('image/')
    if (ok) onPick(f)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false)
    const f = e.dataTransfer.files?.[0]
    if (f) acceptFile(f)
  }

  const pdfMode = isPdf(file)

  return (
    <div
      ref={zoneRef}
      tabIndex={0}
      className={`drop-zone ${drag ? 'drag' : ''} ${file ? 'filled' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_TYPES}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) acceptFile(f)
          if (inputRef.current) inputRef.current.value = ''
        }}
      />
      {file && previewUrl ? (
        <>
          {pdfMode ? (
            <button className="drop-pdf-preview" onClick={() => window.open(previewUrl, '_blank')}>
              <FilePdf size={64} weight="duotone" />
              <strong>PDF cargado</strong>
              <span>Click para previsualizar</span>
            </button>
          ) : (
            <img src={previewUrl} alt={titulo} className="drop-preview" />
          )}
          <div className="drop-overlay">
            <span className="drop-filename">{file.name}</span>
            <button className="drop-remove" onClick={(e) => { e.stopPropagation(); onRemove() }} title="Quitar">
              <X size={14} />
            </button>
          </div>
        </>
      ) : (
        <button className="drop-empty" onClick={() => inputRef.current?.click()}>
          <Icon size={32} weight="duotone" />
          <strong>{titulo}</strong>
          <span>{sub}</span>
          <small>Click, arrastrá o pegá</small>
        </button>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso intermedio · Procesando OCR
// ════════════════════════════════════════════════════════════════════

function PasoProcesando() {
  return (
    <div className="paso paso-procesando">
      <div className="iso-spinner" aria-hidden="true">
        <img src="/brand/isotipo-naranja.svg" alt="" />
      </div>
      <h2>Verificando documentos del asociado</h2>
      <p>Estamos validando la identidad de forma segura. Toma unos segundos.</p>
      <ul className="procesando-steps">
        <li>Leyendo el DPI</li>
        <li>Validando autenticidad</li>
        <li>Confirmando domicilio del recibo</li>
        <li>Revisando si ya existe en la cooperativa</li>
      </ul>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso especial · Duplicado encontrado
// ════════════════════════════════════════════════════════════════════

function PasoDuplicado({
  dup, onVerFicha, onReintentar,
}: {
  dup: { id: string; nombre: string; no: string }
  onVerFicha: () => void
  onReintentar: () => void
}) {
  return (
    <div className="paso paso-duplicado">
      <Warning size={48} weight="duotone" color="var(--warning)" />
      <h2>Este DPI ya está registrado</h2>
      <div className="dup-card">
        <p className="dup-label">Asociado existente</p>
        <p className="dup-nombre">{dup.nombre}</p>
        <p className="dup-no">#{dup.no}</p>
      </div>
      <p className="dup-info">No podés crear un nuevo asociado con un DPI que ya existe. Podés ver la ficha del actual o intentar con otra persona.</p>
      <div className="paso-footer">
        <button className="btn-secondary" onClick={onReintentar}>
          <ArrowLeft size={18} /> Capturar otra persona
        </button>
        <button className="btn-primary" onClick={onVerFicha}>
          Ver ficha de {dup.nombre.split(' ')[0]} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso 2 · Revisar y completar
// ════════════════════════════════════════════════════════════════════

type PasoRevisarProps = {
  extracted: OcrExtracted
  images: Partial<Record<DocSlot, File>>
  previewUrls: Partial<Record<DocSlot, string>>
  form: FormOperativo
  setForm: React.Dispatch<React.SetStateAction<FormOperativo>>
  pin: string
  onRegenerarPin: () => void
  onVolver: () => void
  onCrear: () => void
  error: string | null
}

function PasoRevisar(p: PasoRevisarProps) {
  const set = <K extends keyof FormOperativo>(k: K, v: FormOperativo[K]) =>
    p.setForm(f => ({ ...f, [k]: v }))

  const dpiFormateado = formatCui(p.extracted.numero_documento)
  const mrzOk = p.extracted._meta.mrz.composite_check_valid && p.extracted._meta.mrz.document_check_valid

  return (
    <div className="paso paso-revisar">
      {/* Banner DPI extraído */}
      <div className={`mrz-banner ${mrzOk ? 'ok' : 'warn'}`}>
        <div>
          <span className="mrz-label">DPI extraído</span>
          <span className="mrz-dpi">{dpiFormateado}</span>
        </div>
        <div className="mrz-status">
          {mrzOk ? (
            <><ShieldCheck size={20} weight="fill" /> MRZ validada</>
          ) : (
            <><Warning size={20} weight="fill" /> MRZ con discrepancias — revisá</>
          )}
        </div>
      </div>

      <div className="revisar-grid">
        {/* IZQUIERDA · Imágenes / PDFs */}
        <aside className="revisar-docs">
          <h3>Documentos</h3>
          {(['dpi_frente', 'dpi_dorso', 'recibo'] as DocSlot[]).map(slot => {
            const file = p.images[slot]
            const url = p.previewUrls[slot]
            return (
              <div key={slot} className="doc-thumb">
                <p className="doc-thumb-label">{DOC_LABELS[slot].titulo}</p>
                {url && (
                  isPdf(file ?? null) ? (
                    <a href={url} target="_blank" rel="noreferrer" className="doc-thumb-pdf">
                      <FilePdf size={28} weight="duotone" />
                      <span>{file?.name ?? 'documento.pdf'}</span>
                      <small>Click para abrir</small>
                    </a>
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt={slot} />
                    </a>
                  )
                )}
              </div>
            )
          })}
        </aside>

        {/* DERECHA · Formulario */}
        <section className="revisar-form">
          <FormSection title="Identificación" subtitle="Datos del DPI (extraídos del MRZ y del frente)">
            <Field label="Primer nombre" required value={p.form.primer_nombre} onChange={v => set('primer_nombre', v)} source={p.extracted._meta.sources.primer_nombre} />
            <Field label="Segundo nombre" value={p.form.segundo_nombre} onChange={v => set('segundo_nombre', v)} source={p.extracted._meta.sources.segundo_nombre} />
            <Field label="Otros nombres" value={p.form.otros_nombres} onChange={v => set('otros_nombres', v)} />
            <Field label="Primer apellido" required value={p.form.primer_apellido} onChange={v => set('primer_apellido', v)} source={p.extracted._meta.sources.primer_apellido} />
            <Field label="Segundo apellido" value={p.form.segundo_apellido} onChange={v => set('segundo_apellido', v)} source={p.extracted._meta.sources.segundo_apellido} />
            <Field label="Apellido de casada" value={p.form.apellido_casada} onChange={v => set('apellido_casada', v)} />
            <Field label="Fecha de nacimiento" type="date" required value={p.form.fecha_nacimiento} onChange={v => set('fecha_nacimiento', v)} source={p.extracted._meta.sources.fecha_nacimiento} />
            <Field label="Sexo" required as="select" value={p.form.sexo} onChange={v => set('sexo', v as Genero)} options={[{ v: '', l: '—' }, { v: 'MASCULINO', l: 'Masculino' }, { v: 'FEMENINO', l: 'Femenino' }]} />
            <Field label="Estado civil" required as="select" value={p.form.estado_civil} onChange={v => set('estado_civil', v as EstadoCivil)} options={[{ v: '', l: '—' }, { v: 'SOLTERO', l: 'Soltero/a' }, { v: 'CASADO', l: 'Casado/a' }, { v: 'UNIDO', l: 'Unido/a' }, { v: 'DIVORCIADO', l: 'Divorciado/a' }, { v: 'VIUDO', l: 'Viudo/a' }]} />
            <Field label="Nacionalidad" required value={p.form.nacionalidad} onChange={v => set('nacionalidad', v)} />
            <Field label="País de nacimiento" required value={p.form.pais_nacimiento} onChange={v => set('pais_nacimiento', v)} />
            <Field label="Departamento de nacimiento" required value={p.form.departamento_nacimiento} onChange={v => set('departamento_nacimiento', v)} />
            <Field label="Municipio de nacimiento" required value={p.form.municipio_nacimiento} onChange={v => set('municipio_nacimiento', v)} />
            <Field label="Vigencia del DPI" type="date" required value={p.form.vigencia_documento} onChange={v => set('vigencia_documento', v)} source={p.extracted._meta.sources.vigencia_documento} />
          </FormSection>

          <FormSection title="Domicilio" subtitle="Extraído del recibo de servicios">
            <Field label="Dirección completa" required value={p.form.direccion_completa} onChange={v => set('direccion_completa', v)} source={p.extracted._meta.sources.direccion_completa} cols={2} />
            <Field label="Zona" value={p.form.zona} onChange={v => set('zona', v)} />
            <Field label="Municipio" value={p.form.municipio} onChange={v => set('municipio', v)} />
            <Field label="Departamento" value={p.form.departamento} onChange={v => set('departamento', v)} />
          </FormSection>

          <FormSection title="Cumplimiento y contacto" subtitle="Lo que solo el operador puede llenar">
            <div className="field span-2">
              <label className="field-label">Profesión u oficio <span className="req">*</span></label>
              <ProfesionSelect
                value={p.form.profesion_oficio}
                onChange={(nombre) => set('profesion_oficio', nombre)}
              />
              <small className="field-hint">Buscá por nombre o sinónimo. Lenguaje guatemalteco.</small>
            </div>
            <div className="field span-2">
              <label className="field-label">Actividad económica <span className="req">*</span></label>
              <ActividadEconomicaSelect
                value={p.form.actividad_economica}
                onChange={(nombre) => set('actividad_economica', nombre)}
              />
              <small className="field-hint">El sector y actividad del asociado. Compatible con reportería IVE.</small>
            </div>
            <div className="field span-2">
              <label className="field-label">Origen de fondos <span className="req">*</span></label>
              <OrigenFondosCards
                value={p.form.origen_fondos}
                onChange={(v) => set('origen_fondos', v as OrigenFondos)}
              />
            </div>
            <div className="field span-2">
              <label className="field-label">Rango de ingresos mensuales <span className="req">*</span></label>
              <RangoIngresoRadios
                value={p.form.ingresos_mensuales_aprox}
                onChange={(v) => set('ingresos_mensuales_aprox', v)}
              />
            </div>
            <Field label="Email" type="email" value={p.form.email} onChange={v => set('email', v)} />
            <div className="field">
              <label className="field-label">Teléfono móvil</label>
              <TelefonoMultiPaisInput
                value={p.form.telefono_movil}
                onChange={(nacional) => set('telefono_movil', nacional)}
              />
              <small className="field-hint">El teléfono es el usuario de login del app.</small>
            </div>
            <div className="pep-row">
              <label><input type="checkbox" checked={p.form.es_pep} onChange={e => set('es_pep', e.target.checked)} /> Es PEP</label>
              <label><input type="checkbox" checked={p.form.es_cpe} onChange={e => set('es_cpe', e.target.checked)} /> Es CPE</label>
              <label><input type="checkbox" checked={p.form.es_familiar_pep} onChange={e => set('es_familiar_pep', e.target.checked)} /> Familiar PEP</label>
            </div>
          </FormSection>

          <FormSection title="Cuenta del asociado · acceso al app" subtitle="El No. de asociado (Neo ID) se asigna automáticamente al crear. El asociado entra al app con su DPI o teléfono y el PIN provisional, que debe cambiar al primer login.">
            <div className="field">
              <label className="field-label">No. de asociado · Neo ID <span className="source-badge src-mrz">auto</span></label>
              <div className="readonly-pill">Se asigna al crear</div>
              <small className="field-hint">Identificador único de 6 dígitos. Lo genera el sistema en el momento de la inscripción.</small>
            </div>
            <div className="field">
              <label className="field-label">Identificadores para login en el app</label>
              <div className="readonly-pill">
                <IdentificationCard size={14} weight="duotone" /> DPI: <strong>{formatCui(p.extracted.numero_documento)}</strong>
              </div>
              {p.form.telefono_movil && (
                <div className="readonly-pill" style={{ marginTop: 6 }}>
                  <DeviceMobile size={14} weight="duotone" /> Tel: <strong>{p.form.telefono_movil}</strong>
                </div>
              )}
              <small className="field-hint">El asociado puede iniciar sesión con cualquiera de los dos.</small>
            </div>
            <div className="pin-row">
              <label className="field-label">PIN provisional</label>
              <div className="pin-display">
                <code>{p.pin}</code>
                <button type="button" className="btn-link" onClick={p.onRegenerarPin}>
                  <ArrowsClockwise size={14} /> Regenerar
                </button>
              </div>
              <small className="field-hint">Al primer login en el app, el asociado debe cambiarlo por uno propio.</small>
            </div>
          </FormSection>
        </section>
      </div>

      {p.error && <div className="alert alert-error"><Warning size={18} /> {p.error}</div>}

      <footer className="paso-footer">
        <button className="btn-secondary" onClick={p.onVolver}><ArrowLeft size={18} /> Volver</button>
        <button className="btn-primary" onClick={p.onCrear}>
          <CheckCircle size={18} weight="fill" /> Crear asociado y generar acceso
        </button>
      </footer>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso intermedio · Creando asociado
// ════════════════════════════════════════════════════════════════════

function PasoCreando() {
  return (
    <div className="paso paso-procesando">
      <div className="iso-spinner" aria-hidden="true">
        <img src="/brand/isotipo-naranja.svg" alt="" />
      </div>
      <h2>Creando asociado y generando acceso</h2>
      <p>Estamos asignando tu Neo ID y preparando tu cuenta del app.</p>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso 3 · Credenciales
// ════════════════════════════════════════════════════════════════════

function PasoCredenciales({
  resultado, nombreCompleto, onCobrar, onSaltarCobro,
}: {
  resultado: { asociadoId: string; dpi: string; pin: string; email: string; neoId: string }
  nombreCompleto: string
  onCobrar: () => void
  onSaltarCobro: () => void
}) {
  const [copiado, setCopiado] = useState(false)
  const texto =
    `Bienvenido a Neocoop.\n\n` +
    `Tu No. de asociado es ${resultado.neoId}.\n\n` +
    `Para entrar al app móvil:\n` +
    `  DPI: ${formatCui(resultado.dpi)}\n` +
    `  PIN provisional: ${resultado.pin}\n\n` +
    `Al primer login te pediremos cambiar el PIN por uno tuyo.`

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2200)
    } catch {}
  }

  return (
    <div className="paso paso-credenciales">
      <div className="check-circle"><CheckCircle size={48} weight="fill" /></div>
      <h2>{nombreCompleto || 'Asociado'} quedó como prospecto</h2>
      <p>Le generamos sus credenciales. Para que sea asociado activo falta cobrar la cuota de ingreso + aportación inicial (Q100).</p>

      <div className="cred-card">
        <div className="cred-row"><span>No. asociado · Neo ID</span><strong>{resultado.neoId}</strong></div>
        <div className="cred-row"><span>DPI (login)</span><strong>{formatCui(resultado.dpi)}</strong></div>
        <div className="cred-row"><span>PIN provisional</span><strong className="pin-big">{resultado.pin}</strong></div>
        <div className="cred-note">
          ⚠️ El asociado debe cambiar el PIN al primer login (el sistema lo fuerza).
        </div>
      </div>

      <div className="cred-actions">
        <button className="btn-secondary" onClick={copiar}>
          <Copy size={18} /> {copiado ? '¡Copiado!' : 'Copiar al portapapeles'}
        </button>
        <button className="btn-secondary" onClick={() => window.print()}>
          <Printer size={18} /> Imprimir credenciales
        </button>
      </div>

      <footer className="paso-footer">
        <button className="btn-secondary" onClick={onSaltarCobro}>
          Cobrar más tarde <ArrowRight size={18} />
        </button>
        <button className="btn-primary" onClick={onCobrar}>
          <CheckCircle size={18} weight="fill" /> Cobrar inscripción (Q100)
        </button>
      </footer>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Paso 4 · Asociado activo
// ════════════════════════════════════════════════════════════════════

function PasoActivo({
  resultado, onVerFicha, onNuevoAsociado,
}: {
  resultado: { asociadoId: string; dpi: string; pin: string; email: string; neoId: string }
  onVerFicha: () => void
  onNuevoAsociado: () => void
}) {
  return (
    <div className="paso paso-credenciales">
      <div className="check-circle activo"><CheckCircle size={48} weight="fill" /></div>
      <h2>¡Asociado activo!</h2>
      <p>El cobro de inscripción se registró y se crearon las cuentas de aportaciones y ahorro corriente. El asociado ya puede operar en el app móvil.</p>

      <div className="cred-card">
        <div className="cred-row"><span>No. asociado · Neo ID</span><strong>{resultado.neoId}</strong></div>
        <div className="cred-row"><span>Estado</span><strong style={{ color: 'var(--success)' }}>ACTIVO</strong></div>
      </div>

      <footer className="paso-footer">
        <button className="btn-secondary" onClick={onNuevoAsociado}>
          <User size={18} /> Inscribir otro
        </button>
        <button className="btn-primary" onClick={onVerFicha}>
          Ver ficha del asociado <ArrowRight size={18} />
        </button>
      </footer>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Componentes utilitarios
// ════════════════════════════════════════════════════════════════════

function FormSection({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="form-section">
      <header><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</header>
      <div className="form-grid">{children}</div>
    </section>
  )
}

type FieldProps = {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'email' | 'date' | 'number'
  required?: boolean
  source?: FieldSource | undefined
  as?: 'input' | 'select'
  options?: Array<{ v: string; l: string }>
  cols?: 1 | 2
}

function Field(p: FieldProps) {
  return (
    <div className={`field ${p.cols === 2 ? 'span-2' : ''}`}>
      <label className="field-label">
        {p.label} {p.required && <span className="req">*</span>}
        {p.source && <SourceBadge source={p.source} />}
      </label>
      {p.as === 'select' ? (
        <select value={p.value} onChange={e => p.onChange(e.target.value)}>
          {p.options?.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input type={p.type || 'text'} value={p.value} onChange={e => p.onChange(e.target.value)} />
      )}
    </div>
  )
}

function SourceBadge({ source }: { source: FieldSource }) {
  const label = source === 'mrz' ? 'MRZ' : source === 'frente' ? 'Frente' : source === 'dorso' ? 'Dorso' : source === 'recibo' ? 'Recibo' : 'Manual'
  return <span className={`source-badge src-${source}`}>{label}</span>
}

// ════════════════════════════════════════════════════════════════════
// Cards de origen de fondos · replica del rediseño móvil (jul 2026)
// Consistencia con app/app/asociarme/confirmar-contacto.tsx
// ════════════════════════════════════════════════════════════════════

type OrigenOption = {
  value: OrigenFondos
  titulo: string
  descripcion: string
  Icon: typeof Briefcase
}

const ORIGENES_OPTIONS: OrigenOption[] = [
  { value: 'SALARIO' as OrigenFondos,                 titulo: 'Sueldo o salario',       descripcion: 'Trabajás para una empresa.',       Icon: Briefcase },
  { value: 'NEGOCIO_PROPIO' as OrigenFondos,          titulo: 'Negocio propio',         descripcion: 'Tenés algo propio.',                Icon: Storefront },
  { value: 'HONORARIOS_PROFESIONALES' as OrigenFondos, titulo: 'Trabajo por mi cuenta',  descripcion: 'Freelance, oficio independiente.',  Icon: Star },
  { value: 'REMESAS' as OrigenFondos,                 titulo: 'Remesas de familia',     descripcion: 'Te llega dinero del exterior.',     Icon: Airplane },
  { value: 'INVERSIONES' as OrigenFondos,             titulo: 'Rentas o inversiones',   descripcion: 'Alquileres, dividendos, plazos.',   Icon: Bank },
  { value: 'PENSION_JUBILACION' as OrigenFondos,      titulo: 'Pensión o jubilación',   descripcion: 'Retirado o jubilado.',              Icon: Armchair },
  { value: 'HERENCIA_DONACION' as OrigenFondos,       titulo: 'Herencia o donación',    descripcion: 'Recibido de familia o terceros.',   Icon: DotsThree },
  { value: 'OTROS' as OrigenFondos,                   titulo: 'Otra fuente',            descripcion: 'Ventas, ganancias varias.',          Icon: DotsThree },
]

function OrigenFondosCards({
  value,
  onChange,
}: {
  value: OrigenFondos
  onChange: (v: OrigenFondos) => void
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
      {ORIGENES_OPTIONS.map((o) => {
        const active = value === o.value
        const Icon = o.Icon
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 14,
              background: active ? 'rgba(240,78,35,0.06)' : '#FFFFFF',
              border: active ? '1.5px solid #F04E23' : '1px solid var(--hairline, rgba(17,17,17,0.10))',
              borderRadius: 12,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: active ? 'rgba(240,78,35,0.12)' : 'rgba(17,17,17,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon size={20} color={active ? '#F04E23' : '#4a4238'} weight="duotone" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1714' }}>{o.titulo}</div>
              <div style={{ fontSize: 12, color: '#6B6B6B', marginTop: 2 }}>{o.descripcion}</div>
            </div>
            {active && (
              <div style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                background: '#F04E23',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Check size={14} color="white" weight="bold" />
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Radio buttons de rango de ingreso
// ════════════════════════════════════════════════════════════════════

function RangoIngresoRadios({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6 }}>
      {RANGOS_INGRESO.map((r) => {
        const active = value === r.value
        return (
          <button
            key={r.value}
            type="button"
            onClick={() => onChange(r.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              background: active ? 'rgba(240,78,35,0.06)' : '#FFFFFF',
              border: active ? '1.5px solid #F04E23' : '1px solid var(--hairline, rgba(17,17,17,0.10))',
              borderRadius: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            <span style={{ fontSize: 14, color: '#1A1714', fontWeight: active ? 600 : 400 }}>
              {r.label}
            </span>
            <div style={{
              width: 20,
              height: 20,
              borderRadius: 10,
              border: active ? '6px solid #F04E23' : '1.5px solid rgba(17,17,17,0.20)',
              background: active ? 'white' : 'transparent',
              boxSizing: 'border-box',
              flexShrink: 0,
            }} />
          </button>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Teléfono con selector multi-país
// Consistente con EditarTelefonoAppModal (ficha del asociado) y
// confirmar-contacto.tsx (app móvil).
// ════════════════════════════════════════════════════════════════════

function TelefonoMultiPaisInput({
  value,
  onChange,
}: {
  value: string
  onChange: (nacional: string) => void
}) {
  const [pais, setPais] = useState<Pais>(() => {
    if (value?.startsWith('+')) return detectarPais(value)
    return PAIS_DEFAULT
  })

  const nacional = value?.startsWith('+') ? digitosNacionales(value, pais) : (value ?? '')

  const onPaisChange = (iso: string) => {
    const p = PAISES.find((x) => x.iso === iso) ?? PAIS_DEFAULT
    setPais(p)
    if (nacional && nacional.length >= p.minLen && nacional.length <= p.maxLen) {
      onChange(armarE164(p, nacional))
    } else {
      onChange('')
    }
  }

  const onNacionalChange = (raw: string) => {
    const clean = raw.replace(/\D/g, '').slice(0, pais.maxLen)
    if (clean.length >= pais.minLen) {
      onChange(armarE164(pais, clean))
    } else {
      onChange(clean)
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      background: '#FFFFFF',
      border: '1px solid var(--hairline, rgba(17,17,17,0.10))',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '0 10px 0 12px',
        borderRight: '1px solid var(--hairline, rgba(17,17,17,0.10))',
        background: 'rgba(17,17,17,0.02)',
      }}>
        <span style={{ fontSize: 18 }}>{pais.bandera}</span>
        <span style={{ fontSize: 13, color: '#6B6B6B', fontWeight: 500 }}>+{pais.prefijoDigits}</span>
        <CaretDown size={12} color="#A8A8A8" weight="bold" />
        <select
          value={pais.iso}
          onChange={(e) => onPaisChange(e.target.value)}
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {PAISES.map((p) => (
            <option key={p.iso} value={p.iso}>
              {p.bandera} {p.nombre} · +{p.prefijoDigits}
            </option>
          ))}
        </select>
      </div>
      <input
        type="tel"
        value={nacional}
        onChange={(e) => onNacionalChange(e.target.value)}
        placeholder={pais.placeholder}
        maxLength={pais.maxLen}
        style={{
          flex: 1,
          padding: '10px 12px',
          border: 'none',
          background: 'transparent',
          fontSize: 15,
          fontFamily: 'inherit',
          color: '#1A1714',
          outline: 'none',
        }}
      />
    </div>
  )
}
